import { useForm, useWatch } from "antd/es/form/Form";
import React from "react";
import { useIntl } from "react-intl";
import {
  useAssignLicenseKeyToMember,
  useAssignLicenseKeyToNewMember,
  useCheckLicenseKey
} from "services/rest/subscription";
import { Form, Input, Modal, notification, Radio } from "antd";
import { RoleSelect } from "components/role-select/RoleSelect";
import { UserTeamSelect } from "components/user-team-select/UserTeamSelect";
import { ILicenseKey } from "components/license-keys-card/LicenseKyesCard";
import { UserType } from "components/license-keys-card/utils/utils";
import { FormatUtils } from "services/utils/FormatUtils";
import { AxiosResponse } from "axios";
import { ValidateCode, ValidateResponse } from "services/rest/general";

export type AssignLicenseKeyModalProps = {
  title: string,
  licenseKey: ILicenseKey,
  subscriptionId: string,
  visible: boolean,
  onAfterOk: () => void,
  onCancel: () => void,
}

export function AssignModal(props: AssignLicenseKeyModalProps) {
  const { title, subscriptionId, licenseKey, onAfterOk, onCancel } = props;
  const intl = useIntl();
  const [form] = useForm();
  const type = useWatch("type", form);
  const email = useWatch("email", form);
  const memberId = useWatch("user", form);

  const {
    loading: assignToMemberLoading,
    fetch: assignToMemberFetch
  } = useAssignLicenseKeyToMember(subscriptionId, licenseKey.id);
  const {
    loading: assignToNewMemberLoading,
    fetch: assignToNewMemberFetch
  } = useAssignLicenseKeyToNewMember(subscriptionId, licenseKey.id);

  const {
    fetch: checkLicenseFetch,
    loading: checkLicenseLoading,
  } = useCheckLicenseKey(subscriptionId, licenseKey.id, memberId, email, [memberId]);

  const validateEmail = () => {
    return checkLicenseFetch()
      .then((response: AxiosResponse<ValidateResponse>) => {
        const code = response.data.code;

        if (code == ValidateCode.CUSTOMER_IN_ANOTHER_COMPANY) {
          return Promise.reject(intl.formatMessage({ id: "jmix-store.assign-modal.validate.in-another-company" }));
        } else if (code == ValidateCode.CUSTOMER_IN_THIS_COMPANY) {
          return Promise.reject(intl.formatMessage({ id: "jmix-store.assign-modal.validate.in-this-company" }));
        } else if (code == ValidateCode.MEMBER_HAS_LICENSE_KEY) {
          return Promise.reject(intl.formatMessage({ id: "jmix-store.assign-modal.validate.has-license-key" }));
        } else {
          return Promise.resolve();
        }
      });
  };

  const validateLicense = () => {
    return checkLicenseFetch()
      .then((response: AxiosResponse<ValidateResponse>) => {
        const code = response.data.code;

        if (code == ValidateCode.MEMBER_HAS_LICENSE_KEY) {
          return Promise.reject(intl.formatMessage({ id: "jmix-store.assign-modal.validate.has-license-key" }));
        } else {
          return Promise.resolve();
        }
      });
  };

  const newUserForm = (
    <>
      <Form.Item
        label={intl.formatMessage({ id: "jmix-store.team-member.column.email" })}
        name="email"
        required
        rules={[
          {
            required: true,
            message: intl.formatMessage({ id: "jmix-store.company-billing-info.email.rule-message_1" }),
          },
          {
            type: "email",
            validateTrigger: "onSubmit",
            message: intl.formatMessage({ id: "jmix-store.team-member.email.not-valid" }),
          },
          {
            validateTrigger: "onSubmit",
            transform: (value: string) => value.replace(/\s+/g, "").toLowerCase(),
            validator: () => validateEmail(),
          }
        ]}
      >
        <Input onBlur={(value) => form.setFieldsValue({ email: FormatUtils.getEmailFormat(value.target.value) })} />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({ id: "jmix-store.team-member.column.role" })}
        name="role"
        required
        rules={[
          {
            required: true,
            message: intl.formatMessage({ id: "jmix-store.team-member.role.rule-message" }),
          }
        ]}
      >
        <RoleSelect />
      </Form.Item>
    </>
  );

  const teamUserForm = (
    <Form.Item
      label={intl.formatMessage({ id: "jmix-store.license-keys-card.column.user" })}
      name="user"
      required
      rules={[
        {
          required: true,
          message: intl.formatMessage({ id: "jmix-store.assign-modal.user.rule-message" }),
        },
        {
          validateTrigger: "onSubmit",
          validator: () => validateLicense(),
        }
      ]}
    >
      <UserTeamSelect />
    </Form.Item>
  );

  const assignLicenseKeyToMember = () => {
    assignToMemberFetch({
      memberId: form.getFieldValue("user"),
    })
      .then(() => {
        Modal.success({
          centered: true,
          title: intl.formatMessage({ id: "jmix-store.account.add-user-modal.title" }),
          content: (
            <>
              <div>
                {intl.formatMessage({ id: "jmix-store.assign-modal.success.content_1" })}
              </div>
              <div>
                {intl.formatMessage({ id: "jmix-store.assign-modal.success.content_2" })}
              </div>
            </>
          )
        });

        onCancel();
        onAfterOk();
      })
      .catch(() => {
        notification.error({
          message: intl.formatMessage({ id: "jmix-store.assign-modal.error" }),
          placement: "bottomLeft",
        });
      });
  };

  const assignLicenseKeyToNewMember = () => {
    assignToNewMemberFetch({
      ...form.getFieldsValue()
    })
      .then(() => {
        Modal.success({
          centered: true,
          title: intl.formatMessage({ id: "jmix-store.account.add-user-modal.title" }),
          content: (
            <>
              <div>
                {intl.formatMessage(
                  { id: "jmix-store.assign-modal.success-new.content_1" },
                  { email: <strong>{email}</strong> }
                )}
              </div>
              <div>
                {intl.formatMessage({ id: "jmix-store.assign-modal.success-new.content_2" })}
              </div>
            </>
          )
        });

        onCancel();
        onAfterOk();
      })
      .catch(() => {
        notification.error({
          message: intl.formatMessage({ id: "jmix-store.assign-modal.error" }),
          placement: "bottomLeft",
        });
      });
  };

  return (
    <Modal
      centered
      destroyOnClose
      title={title}
      visible={props.visible}
      okButtonProps={{
        loading: assignToNewMemberLoading || assignToMemberLoading || checkLicenseLoading
      }}
      onOk={() => {
        form.validateFields()
          .then(() => {
            if (type == UserType.TEAM) {
              assignLicenseKeyToMember();
            } else {
              assignLicenseKeyToNewMember();
            }
          });
      }}
      onCancel={onCancel}
    >
      <Form
        layout="vertical"
        preserve={false}
        form={form}
      >
        <Form.Item>
          <strong>{intl.formatMessage({ id: "jmix-store.subscription.re-assign-license-modal.key" })}</strong>
          <span>{licenseKey.keyId}</span>
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ id: "jmix-store.assign-modal.assign-to" })}
          name="type"
          initialValue={UserType.TEAM}
        >
          <Radio.Group>
            <Radio.Button
              value={UserType.TEAM}
            >
              {intl.formatMessage({ id: "jmix-store.assign-modal.user-from-team" })}
            </Radio.Button>
            <Radio.Button
              value={UserType.NEW}
            >
              {intl.formatMessage({ id: "jmix-store.assign-modal.new-user" })}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
        {type === UserType.NEW ? newUserForm : teamUserForm}
      </Form>
    </Modal>
  );
}